// Delete-original-from-PC engine for the upload modal (File System Access API, Chromium).
// v5.9.2: permissions requested up front; remove() only on a granted handle after a
// server-sha256-matched upload. v5.10.1 (Henry 2026-10-07): the real meaning of remove()'s
// InvalidStateError, one retry, and a freshness check before that retry. v5.10.2: a delete that
// succeeds on that retry is reported with retried: true so it can be logged (and counted) apart.
//
// Pure module — no React, no imports — so it runs under plain node for tests
// (frontend/src/deleteOriginal.test.mjs; not matched by CRA's jest, never bundled).
//
// Why InvalidStateError: Chromium's NativeFileUtil::DeleteFile returns FILE_ERROR_FAILED whenever
// the OS refuses the delete, and Blink turns FILE_ERROR_FAILED into InvalidStateError with the
// generic text "…the state had changed since it was read from disk." On Windows that is almost
// always a file held open by another program (a PDF in Acrobat, the Explorer preview pane). It is
// NOT a change to the bytes — before 5.10.1 it was mislabelled "file changed on disk".

// Fixed, greppable reasons — a file is never skipped silently.
export const DELETE_REASON = {
  BLOCKED: 'File editing blocked for this site', // NotAllowedError
  ACTIVATION: 'activation expired',               // SecurityError: user activation required
  DENIED: 'permission denied',
  LOST: 'permission lost between grant and delete',
  UNAVAILABLE: 'requestPermission unavailable',
  OPEN: 'Windows would not delete this file — it is probably open in another program (Acrobat, preview pane). Close it, then click Delete remaining originals.',
  CHANGED: 'file changed on disk after upload', // a TRUE change: fresh size or sha256 differs
};

export const RETRY_DELAY_MS = 2000;

export const permissionErrorReason = (err) => {
  if (err?.name === 'NotAllowedError') return DELETE_REASON.BLOCKED;
  if (err?.name === 'SecurityError') return DELETE_REASON.ACTIVATION;
  return DELETE_REASON.DENIED;
};

// remove() failures that mean "the OS would not delete it right now" (file open / locked).
export const isFileInUseError = (err) => err?.name === 'InvalidStateError' || err?.name === 'NotReadableError';

const errLabel = (err) => `${err?.name || 'Error'}: ${err?.message || String(err)}`;

// Ask read-write permission for each item's handle, in order, BEFORE any upload work.
// Returns { [itemId]: { granted: true } | { granted: false, reason } }. On the first denial or
// SecurityError/NotAllowedError it stops asking (no further prompts) and gives every remaining
// item that same reason. (Moved unchanged from UploadModal.jsx, v5.9.2.)
export const requestDeletePermissions = async (items) => {
  const result = {};
  let stopReason = null;
  for (const item of items) {
    if (stopReason) { result[item.id] = { granted: false, reason: stopReason }; continue; }
    const h = item.handle;
    if (!h || typeof h.queryPermission !== 'function' || typeof h.requestPermission !== 'function') {
      result[item.id] = { granted: false, reason: DELETE_REASON.UNAVAILABLE };
      continue;
    }
    try {
      let state = await h.queryPermission({ mode: 'readwrite' });
      if (state !== 'granted') state = await h.requestPermission({ mode: 'readwrite' });
      if (state === 'granted') {
        result[item.id] = { granted: true };
      } else {
        stopReason = DELETE_REASON.DENIED;
        result[item.id] = { granted: false, reason: stopReason };
      }
    } catch (err) {
      stopReason = permissionErrorReason(err);
      result[item.id] = { granted: false, reason: stopReason };
    }
  }
  return result;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Delete ONE original whose upload is already confirmed (server sha256 matched).
//   handle   FileSystemFileHandle (queryPermission / remove / getFile)
//   perm     this item's result from requestDeletePermissions
//   expected { sha256, size } of what was uploaded
//   hash     async (File) => sha256 hex (the modal passes computeSha256)
// Returns { outcome: 'deleted' | 'skipped' | 'failed', reason, log, retried }:
//   retried = true only when the delete succeeded on the one retry (v5.10.2), else false;
//   reason = the row's text (null when deleted);
//   log    = the audit error text for files.logClientDelete (null when deleted). Skips are logged as
//            "skipped: <reason>" (as in 5.9.2); failures as "<reason> [<ErrorName>: <raw message>]".
// No permission is ever requested here (no prompt, no activation needed).
export async function deleteOriginal({ handle, perm, expected, hash, wait = sleep, retryDelayMs = RETRY_DELAY_MS }) {
  const skipped = (reason) => ({ outcome: 'skipped', reason, log: `skipped: ${reason}`, retried: false });
  if (!perm || !perm.granted) return skipped(perm?.reason || DELETE_REASON.UNAVAILABLE);
  try {
    const state = await handle.queryPermission({ mode: 'readwrite' });
    if (state !== 'granted') return skipped(DELETE_REASON.LOST);
  } catch (err) {
    return skipped(DELETE_REASON.LOST);
  }

  let first;
  try {
    await handle.remove();
    return { outcome: 'deleted', reason: null, log: null, retried: false };
  } catch (err) {
    first = err;
  }
  if (!isFileInUseError(first)) {
    // Unchanged from 5.9.2: any other error is shown and logged with its raw message.
    const reason = first?.message || 'Could not delete (file may be open elsewhere)';
    return { outcome: 'failed', reason, log: first?.message || String(first), retried: false };
  }

  // The OS refused the delete (file open elsewhere). Wait, prove the file on disk is still exactly
  // what was uploaded, then retry ONCE. Never delete a file that differs from the upload.
  await wait(retryDelayMs);
  try {
    const fresh = await handle.getFile();
    if (!expected || fresh.size !== expected.size) return skipped(DELETE_REASON.CHANGED);
    const freshSha = await hash(fresh);
    if (!expected.sha256 || String(freshSha).toLowerCase() !== String(expected.sha256).toLowerCase()) {
      return skipped(DELETE_REASON.CHANGED);
    }
  } catch (err) {
    // Could not re-read the file to verify it: do not retry the delete; leave it for the button.
    return { outcome: 'failed', reason: DELETE_REASON.OPEN, log: `${DELETE_REASON.OPEN} [first ${errLabel(first)}; re-read ${errLabel(err)}]`, retried: false };
  }
  try {
    await handle.remove();
    return { outcome: 'deleted', reason: null, log: null, retried: true };
  } catch (err) {
    const reason = isFileInUseError(err) ? DELETE_REASON.OPEN : (err?.message || 'Could not delete (file may be open elsewhere)');
    return { outcome: 'failed', reason, log: `${reason} [${errLabel(err)}; retried after ${retryDelayMs} ms]`, retried: false };
  }
}
